import React, { useState } from 'react';
import AdvanceSubscriptionCard from './AdvanceSubscriptionCard';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { Typography } from '@mui/material';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { AllocationRule } from 'further-types/lp/allocations';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';

interface AdvanceSubscription {
  actionId: string;
  entryId: string;
  name: string;
  ledgerDate: CalendarDay;
  actionDueDate?: CalendarDay;
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
  }>;
  newInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
    callOverpaidOrUnderpaid: number;
  }>;
  numberOfEntries: number;
}

interface AdvanceSubscriptionsContentProps {
  partnerTransferId: string;
  advanceSubscriptions: Array<AdvanceSubscription>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
  stepper: React.ReactNode;
}

const AdvanceSubscriptionsContent: React.FC<
  AdvanceSubscriptionsContentProps
> = ({
  partnerTransferId,
  advanceSubscriptions,
  baseCurrency,
  onNext,
  onPrevious,
  stepper,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const [isNavigating, setIsNavigating] = useState(false);
  const updatePartnerTransfer = api.partnerTransfers.update.useMutation(
    useHandleTrpcMutation()({
      onSuccess: () => {
        onNext();
      },
    }),
  );

  const rows = advanceSubscriptions.map((sub) => ({
    ...sub,
    id: sub.actionId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => <Typography variant="body1">{row.name}</Typography>,
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) =>
        row.ledgerDate ? row.ledgerDate.toLocalDateLabel() : '-',
    },
    {
      key: 'totalAmountLcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
  ];

  const handleNext = () => {
    setIsNavigating(true);
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.AdvanceSubscriptions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionAdvanceSubscriptions: true,
    };
    updatePartnerTransfer.mutate(input, {
      onError: () => setIsNavigating(false),
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={handlePrevious}
          onNext={handleNext}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <AdvanceSubscriptionCard
            advanceSubscription={row}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={rows}
        emptyMessage="No advance subscriptions found for this partner transfer."
      />
    </Page>
  );
};

export default AdvanceSubscriptionsContent;
